import React from "react";
import { VStack, Text, HStack, Select, Box } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import {
  StepProps,
  PageLookalikeAudienceFormValues,
} from "../types";

function instagramDisplayName(
  account: StepProps["instagramAccounts"][number] | undefined
): string {
  return String(account?.username || account?.name || "").trim();
}

const PageSelectionStep: React.FC<StepProps> = ({
  pages,
  instagramAccounts,
}) => {
  const form = useFormContext<PageLookalikeAudienceFormValues>();

  const facebookPageId = form.watch("facebookPageId");
  const instagramPageId = form.watch("instagramPageId");

  const handleFacebookPageChange = (value: string) => {
    const selectedPage = pages.find((p) => p.id === value);
    form.setValue("facebookPageId", value);
    form.setValue("facebookPageName", selectedPage?.name || "");

    if (!value) {
      const selectedInstagramId = String(
        form.getValues("instagramPageId") || ""
      ).trim();

      if (selectedInstagramId) {
        const selectedInstagramStillAvailable = instagramAccounts.some(
          (account) => account.id === selectedInstagramId
        );
        if (!selectedInstagramStillAvailable) {
          form.setValue("instagramPageId", "");
          form.setValue("instagramPageName", "");
        }
      }
      return;
    }

    const selectedInstagramId = String(
      form.getValues("instagramPageId") || ""
    ).trim();
    if (
      selectedInstagramId &&
      !instagramAccounts.some((account) => account.id === selectedInstagramId)
    ) {
      form.setValue("instagramPageId", "");
      form.setValue("instagramPageName", "");
    }
  };

  const handleInstagramAccountChange = (value: string) => {
    form.setValue("instagramPageId", value);
    if (value) {
      const selectedAccount = instagramAccounts.find(
        (acc) => acc.id === value
      );
      const username = instagramDisplayName(selectedAccount);
      form.setValue("instagramPageName", username);
    } else {
      form.setValue("instagramPageName", "");
    }
  };

  const selectedFacebookPageId = String(facebookPageId ?? "").trim();
  const selectedInstagramAccountId = String(instagramPageId ?? "").trim();

  return (
    <VStack spacing={6} align="stretch">
      <VStack align="stretch" spacing={4}>
        <HStack spacing={6} align="stretch">
          <Box
            flex={1}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            p={4}
          >
            <VStack align="stretch" spacing={2}>
              <Text fontWeight="500">Facebook Page</Text>
              <Select
                placeholder="Select a Facebook Page"
                fontSize="14px"
                value={selectedFacebookPageId}
                onChange={(e) => {
                  handleFacebookPageChange(e.target.value);
                }}
                boxShadow={"sm"}
              >
                {pages.map((page) => (
                  <option key={page.id} value={page.id}>
                    {page.name}
                  </option>
                ))}
              </Select>
            </VStack>
          </Box>

          <Box
            flex={1}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            p={4}
          >
            <VStack align="stretch" spacing={2}>
              <Text fontWeight="500">Instagram Account</Text>
              <Select
                placeholder="Select an Instagram Account"
                fontSize="14px"
                value={selectedInstagramAccountId}
                onChange={(e) => handleInstagramAccountChange(e.target.value)}
                boxShadow={"sm"}
              >
                {instagramAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    @{instagramDisplayName(account) || account.id}
                  </option>
                ))}
              </Select>
            </VStack>
          </Box>
        </HStack>
      </VStack>
    </VStack>
  );
};

export default PageSelectionStep;
